/* eslint-disable no-nested-ternary */
import React from 'react'

import { useTableContext } from './TableContext'
import { getSortDirection, toggleSortDirection } from './utils'
import column from './interface'

interface TableColProps {
  column: column
}

export const TableCol: React.FC<TableColProps> = ({ column }) => {
  const {
    dispatch,
    sortColumn,
    sortDirection,
    enableMultiSort,
    sortMultiColumns,
    sortMultiDirections,
    defaultSortAsc,
  } = useTableContext()

  const handleSimpleSort = () => {
    const sortActive = column.sortable && sortColumn === column.field
    if (column.sortable) {
      let direction = sortDirection
      // change sort direction only if sortColumn (currently selected column) is === the newly clicked column
      // otherwise, retain sort direction if the column is switched
      if (sortActive) {
        if (sortDirection === 'asc') {
          direction = 'desc'
        } else if (sortDirection === 'desc') {
          direction = 'none'
        } else {
          direction = 'asc'
        }

        // direction = sortDirection === 'asc' ? 'desc' : 'asc'
      }

      dispatch({
        type: 'SORT_CHANGE',
        payload: {
          sortDirection: direction,
          sortColumn: column.field,
          selectedColumn: column,
        },
      })
    }
  }

  // check if column exists in sortMultiColumns
  let colIndex = enableMultiSort ? sortMultiColumns!.indexOf(column.field) : -1

  const handleMultiSort = () => {
    let sortMultiColumnsPayload, sortMultiDirectionsPayload
    // if column doesn't exist add to list
    if (colIndex === -1) {
      sortMultiColumnsPayload = [...sortMultiColumns!, column.field]
      // if it is the 1st column ie primary index, don't change sortMultiDirections as it is already populated with defaultSort
      // else add defaultSort to the sortMultiDirections
      sortMultiDirectionsPayload =
        sortMultiColumns!.length === 0
          ? sortMultiDirections!
          : [...sortMultiDirections!, getSortDirection(defaultSortAsc)]
      colIndex = sortMultiColumns!.length + 1
    } else {
      // if column exits, toggle direction
      sortMultiColumnsPayload = [...sortMultiColumns!]
      sortMultiDirectionsPayload = [
        ...sortMultiDirections!.slice(0, colIndex),
        toggleSortDirection(sortMultiDirections![colIndex]),
        ...sortMultiDirections!.slice(colIndex + 1),
      ]
    }
    dispatch({
      type: 'MULTI_SORT_CHANGE',
      payload: {
        sortMultiColumns: sortMultiColumnsPayload,
        sortMultiDirections: sortMultiDirectionsPayload,
        selectedColumn: column,
      },
    })
  }

  // for sortable columns, set glyph value for unclicked, asc or desc or none
  let sortGlyph = ''
  const nextSortGlyph = (sortOrder: string) => {
    switch (sortOrder) {
      case 'asc':
        return 'cb-sort-asc'
      case 'desc':
        return 'cb-sort-desc'
      default:
        return 'cb-sort'
    }
  }

  if (column.sortable) {
    if (enableMultiSort) {
      if (colIndex === -1) {
        sortGlyph = 'cb-sort'
      } else {
        sortGlyph = nextSortGlyph(sortMultiDirections![colIndex!])
      }
    } else if (column.field === sortColumn) {
      sortGlyph = nextSortGlyph(sortDirection)
    } else {
      sortGlyph = 'cb-sort'
    }
  }

  const sortAria: React.AriaAttributes['aria-sort'] =
    sortGlyph === 'cb-sort-asc' ? 'ascending' : sortGlyph === 'cb-sort-desc' ? 'descending' : 'none'

  const handleSortChange = () => {
    if (!column.sortable) {
      return
    }
    if (!enableMultiSort) {
      handleSimpleSort()
    } else {
      handleMultiSort()
    }
  }
  return (
    <th
      onClick={handleSortChange}
      scope="col"
      className={column.sortable ? 'cb-table-sort-joined' : ''}
      aria-sort={sortAria}
    >
      <div className={`display-flex justify-content-center align-items-center`}>
        <span>{column.label}</span>
        {column.addnClasses && (
          <span className={`cb-margin-left-8 ${column.addnClasses}`} {...column.extraAttrs}></span>
        )}
        {column.sortable && (
          <span className={`cb-glyph ${sortGlyph}`}>
            <span className="sr-only">{sortAria === 'none' ? '...' : `...: activate to sort ${sortAria}`}</span>
          </span>
        )}
      </div>
    </th>
  )
}
